import styled from 'styled-components';
import { SettingsBar } from '@ardoq/settings-bar';
import { SettingsType } from '@ardoq/view-settings';
import { IconName } from '@ardoq/icons';
import { Text } from '@ardoq/typography';
import { Box } from '@ardoq/layout';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { ComponentRepresentation } from '@ardoq/basic-components';
import { RoadmapViewProps, VIEW_ID } from './types';
import { t } from '@ardoq/i18n';
import { roadmapViewStyles } from './roadmapViewStyles';

const RoadmapContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  ${roadmapViewStyles}
`;

const ContentArea = styled(Box)`
  flex: 1;
  overflow: auto;
`;

const EmptyState = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
`;

const RoadmapTable = styled.table`
  width: 100%;
`;

export const RoadmapView = (props: RoadmapViewProps) => {
  return (
    <RoadmapContainer>
      <SettingsBar
        viewId={VIEW_ID}
        leftMenu={[
          {
            id: 'fieldDropdown',
            label: t(
              props.i18nLocale,
              'tabview:roadmap.selectField',
              'Select field'
            ),
            iconName: IconName.FORMAT_LIST_BULLETED,
            options: props.fieldOptions,
            type: SettingsType.DROPDOWN,
          },
        ]}
        rightMenu={props.rightMenu}
        i18nLocale={props.i18nLocale}
      />

      <ContentArea>
        {props.selectedFieldLabel ? (
          <div>
            {props.legend.length > 0 && (
              <div className="legend">
                {props.legend.map(legendItem => (
                  <div
                    key={legendItem.id}
                    className={`item ${legendItem.cssClass}`}
                  >
                    <span className="svgIcon">
                      <div
                        style={{
                          display: 'flex',
                          height: 18,
                          width: 18,
                          alignItems: 'center',
                        }}
                      >
                        <ComponentRepresentation
                          {...legendItem.representationData}
                        />
                      </div>
                    </span>
                    <span className="name">{legendItem.name}</span>
                  </div>
                ))}
              </div>
            )}

            <RoadmapTable data-context-menu={OBJECT_CONTEXT_MENU_NAME}>
              <thead>
                <tr>
                  <th key="empty"></th>
                  {props.selectedFieldValues.map(value => (
                    <th key={value} className="header">
                      {value}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {props.tableRows.map((cells, index) => (
                  <tr key={index}>
                    {cells.map((cell, cellIndex) =>
                      cellIndex === 0 ? (
                        <th key={cellIndex}>{cell.content}</th>
                      ) : (
                        <td key={cellIndex}>
                          {cell.components
                            ? cell.components.map(
                                (component, componentIndex) => (
                                  <div
                                    key={componentIndex}
                                    className={`item ${component.cssClass}`}
                                    id={component.componentId}
                                    data-component-id={component.componentId}
                                  >
                                    <span className="svgIcon">
                                      <div
                                        style={{
                                          display: 'flex',
                                          height: 18,
                                          width: 18,
                                          alignItems: 'center',
                                        }}
                                      >
                                        <ComponentRepresentation
                                          {...component.representationData}
                                        />
                                      </div>
                                    </span>
                                    <span className="name">
                                      {component.content}
                                    </span>
                                  </div>
                                )
                              )
                            : cell.content}
                        </td>
                      )
                    )}
                  </tr>
                ))}
              </tbody>
            </RoadmapTable>
          </div>
        ) : (
          <EmptyState>
            <Text color="textSubtle">
              {t(
                props.i18nLocale,
                'tabview:roadmap.selectFieldPrompt',
                'Please select a field to view the roadmap'
              )}
            </Text>
          </EmptyState>
        )}
      </ContentArea>
    </RoadmapContainer>
  );
};
